import type { StatusDefinition, StatusId } from '../core/types';
import { statuses } from '../data/content';

export interface ActiveStatus {
  id: StatusId;
  stacks: number;
  remaining: number;
  sourceId: string;
  value: number;
}

export class StatusSystem {
  private readonly definitions = new Map<StatusId, StatusDefinition>(statuses.map((item) => [item.id, item]));

  apply(collection: ActiveStatus[], incoming: ActiveStatus): void {
    const definition = this.definition(incoming.id);
    const current = collection.find((item) => item.id === incoming.id);
    if (!current) {
      collection.push({ ...incoming, stacks: Math.min(definition.maxStacks, Math.max(1, incoming.stacks)), remaining: Math.max(1, incoming.remaining) });
      return;
    }
    if (definition.rule === 'stack') current.stacks = Math.min(definition.maxStacks, current.stacks + Math.max(1, incoming.stacks));
    else if (definition.rule === 'replace') current.stacks = Math.min(definition.maxStacks, Math.max(1, incoming.stacks));
    current.remaining = definition.rule === 'stack' ? Math.max(current.remaining, incoming.remaining) : Math.max(1, incoming.remaining);
    current.sourceId = incoming.sourceId;
    current.value = Math.max(current.value, incoming.value);
  }

  tick(collection: ActiveStatus[], timing: 'turn-start' | 'turn-end', onTick: (status: ActiveStatus) => void): void {
    for (const status of [...collection]) {
      const definition = this.definition(status.id);
      if (definition.tick === timing) onTick(status);
      if (timing === 'turn-end') status.remaining -= 1;
    }
    for (let i = collection.length - 1; i >= 0; i -= 1) if (collection[i]!.remaining <= 0) collection.splice(i, 1);
  }

  cleanse(collection: ActiveStatus[]): number {
    const before = collection.length;
    for (let i = collection.length - 1; i >= 0; i -= 1) if (this.definition(collection[i]!.id).kind === 'debuff' && this.definition(collection[i]!.id).cleanseable) collection.splice(i, 1);
    return before - collection.length;
  }

  has(collection: ActiveStatus[], id: StatusId): boolean { return collection.some((item) => item.id === id); }
  stacks(collection: ActiveStatus[], id: StatusId): number { return collection.find((item) => item.id === id)?.stacks ?? 0; }

  private definition(id: StatusId): StatusDefinition {
    const value = this.definitions.get(id);
    if (!value) throw new Error(`Unknown status: ${id}`);
    return value;
  }
}
